import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence, animate, useMotionValue, useTransform } from "framer-motion";
import {
  Monitor, Wifi, Building2, Dumbbell, MoreHorizontal,
  Calculator as CalcIcon,
  Lock, Timer, ShieldCheck,
  Loader2, CheckCircle, X, CalendarCheck,
} from "lucide-react";
import {
  calculateRecovery, formatRand, formatInt, isValidEmail,
} from "../../lib/recovery-calculator";
import { upsertLead, debounce } from "../../lib/leadStore";

// ─── Tokens / animation ───────────────────────────────────────────────────────
const EASE = [0.22, 1, 0.36, 1];
const fadeUp = { hidden: { opacity: 0, y: 18 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } } };
const fadeIn = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { duration: 0.35, ease: EASE } } };
const vp     = { once: true, margin: "-60px" };

// ─── Data ─────────────────────────────────────────────────────────────────────
const BUSINESS_TYPES = [
  { id: "B2B SaaS",       Icon: Monitor },
  { id: "ISP / Fibre",    Icon: Wifi },
  { id: "Property Mgmt",  Icon: Building2 },
  { id: "Gym & Fitness",  Icon: Dumbbell },
  { id: "Other",          Icon: MoreHorizontal },
];

const PROCESSORS = ["PayFast", "Peach Payments", "Ozow", "Other"];

// ─── Cal.com link (read from env so it can be set without code changes) ──────
// Set REACT_APP_CAL_LINK + REACT_APP_CAL_NAMESPACE in /app/frontend/.env.
const CAL_LINK      = process.env.REACT_APP_CAL_LINK      || "";
const CAL_NAMESPACE = process.env.REACT_APP_CAL_NAMESPACE || "";

// ─── Animated rand value (Framer Motion motion-value with formatRand) ────────
function AnimatedRand({ value, fontSize = 38, weight = 800, color = "var(--cl2-t1)" }) {
  const mv = useMotionValue(0);
  const display = useTransform(mv, (v) => formatRand(v));

  useEffect(() => {
    const ctrl = animate(mv, value, { duration: 1.2, ease: EASE });
    return () => ctrl.stop();
  }, [mv, value]);

  return (
    <motion.span
      aria-live="polite"
      style={{
        fontFamily: "'Cabinet Grotesk', 'Outfit', sans-serif",
        fontWeight: weight,
        fontSize,
        color,
        letterSpacing: "-0.025em",
        lineHeight: 1.05,
        display: "inline-block",
      }}
    >
      {display}
    </motion.span>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function Calculator() {
  // ── form state ─────────────────────────────────────────────────────────────
  const [email, setEmail]                             = useState("");
  const [emailTouched, setEmailTouched]               = useState(false);
  const [phone, setPhone]                             = useState("");
  const [businessType, setBusinessType]               = useState(null);
  const [businessTypeCustom, setBusinessTypeCustom]   = useState("");
  const [subscriberCount, setSubscriberCount]         = useState(null);
  const [subscriberInput, setSubscriberInput]         = useState("");
  const [processor, setProcessor]                     = useState("PayFast");
  const [processorCustom, setProcessorCustom]         = useState("");
  const [submitStatus, setSubmitStatus]               = useState("idle"); // idle | loading | success
  const [showCalModal, setShowCalModal]               = useState(false);
  const [showThanks, setShowThanks]                   = useState(false);
  const [showResults, setShowResults]                 = useState(false);
  const [hasApplied, setHasApplied]                   = useState(false);
  const successHoldRef                                = useRef(null);

  // ── derived ────────────────────────────────────────────────────────────────
  const emailValid = isValidEmail(email);
  const showEmailError = emailTouched && email.length > 0 && !emailValid;

  const result = useMemo(
    () => calculateRecovery(businessType, subscriberCount),
    [businessType, subscriberCount]
  );

  const isFormValid =
    emailValid &&
    businessType !== null &&
    subscriberCount !== null &&
    subscriberCount > 0 &&
    (businessType !== "Other" || businessTypeCustom.trim().length > 0) &&
    (processor !== "Other" || processorCustom.trim().length > 0);

  // ── progressive save (debounced upsert on any field change) ────────────────
  const debouncedSaveRef = useRef(
    debounce((payload) => {
      upsertLead(payload, "partial");
    }, 600)
  );

  useEffect(() => {
    if (!emailValid) return;
    debouncedSaveRef.current({
      email,
      phone: phone || null,
      business_type: businessType,
      business_type_custom: businessType === "Other" ? businessTypeCustom : null,
      subscriber_count: subscriberCount,
      payment_processor: processor,
      payment_processor_custom: processor === "Other" ? processorCustom : null,
      calculated_at_risk: result?.monthlyAtRisk ?? null,
      calculated_recovery: result?.conservativeRecovery ?? null,
      calculated_fee: result?.crownLedgerFee ?? null,
      calculated_net: result?.netToClient ?? null,
    });
  }, [
    email, emailValid, phone, businessType, businessTypeCustom,
    subscriberCount, processor, processorCustom, result,
  ]);

  // ── handlers ───────────────────────────────────────────────────────────────
  const onEmailBlur = () => {
    setEmailTouched(true);
    if (isValidEmail(email)) {
      upsertLead({ email }, "partial");
    }
  };

  const onSubscriberChange = (e) => {
    // strip non-digits, allow comma/space input, store as integer
    const raw = e.target.value.replace(/[^0-9]/g, "");
    if (raw === "") {
      setSubscriberInput("");
      setSubscriberCount(null);
      return;
    }
    const n = parseInt(raw, 10);
    setSubscriberCount(n);
    setSubscriberInput(formatInt(n));
  };

  const subscriberAdvisory = useMemo(() => {
    if (subscriberCount && subscriberCount > 500_000)
      return "Contact us directly for enterprise-scale estimates.";
    return null;
  }, [subscriberCount]);

  const handleSubmit = useCallback(async (e) => {
    e?.preventDefault?.();
    if (!isFormValid || submitStatus === "loading") return;

    setShowResults(true);
    setSubmitStatus("loading");

    await upsertLead({
      email,
      phone: phone || null,
      business_type: businessType,
      business_type_custom: businessType === "Other" ? businessTypeCustom : null,
      subscriber_count: subscriberCount,
      payment_processor: processor,
      payment_processor_custom: processor === "Other" ? processorCustom : null,
      calculated_at_risk: result?.monthlyAtRisk ?? null,
      calculated_recovery: result?.conservativeRecovery ?? null,
      calculated_fee: result?.crownLedgerFee ?? null,
      calculated_net: result?.netToClient ?? null,
    }, "submitted");

    setSubmitStatus("success");

    // Hold the success screen for 5s, then open the Cal.com modal.
    clearTimeout(successHoldRef.current);
    successHoldRef.current = setTimeout(() => {
      setShowCalModal(true);
    }, 5000);
  }, [
    isFormValid, submitStatus, email, phone, businessType, businessTypeCustom,
    subscriberCount, processor, processorCustom, result,
  ]);

  const completeApplication = useCallback(() => {
    clearTimeout(successHoldRef.current);
    setShowCalModal(false);
    setShowThanks(true);
    setHasApplied(true);
  }, []);

  // ── Cal.com — listen for bookingSuccessful via the official embed API ──────
  const calRootRef = useRef(null);
  useEffect(() => {
    if (!showCalModal || !CAL_LINK) return;

    let cancelled = false;
    let cleanup = () => {};

    (async () => {
      try {
        const mod = await import("@calcom/embed-react");
        const { getCalApi } = mod;
        const cal = await getCalApi(
          CAL_NAMESPACE ? { namespace: CAL_NAMESPACE } : undefined
        );
        if (cancelled) return;

        // Configure UI to blend with brand
        cal("ui", {
          theme: "dark",
          styles: { branding: { brandColor: "#B8922A" } },
          hideEventTypeDetails: false,
          layout: "month_view",
        });

        const onBooked = () => {
          clearTimeout(successHoldRef.current);
          setShowCalModal(false);
          setShowThanks(true);
          setHasApplied(true);
        };
        cal("on", { action: "bookingSuccessful", callback: onBooked });
        cleanup = () => {
          try { cal("off", { action: "bookingSuccessful", callback: onBooked }); }
          catch { /* noop */ }
        };
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[Calculator] Cal.com embed failed to load", err);
      }
    })();

    return () => { cancelled = true; cleanup(); };
  }, [showCalModal]);

  // ── ESC closes modals ──────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (showThanks) setShowThanks(false);
      else if (showCalModal) setShowCalModal(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showCalModal, showThanks]);

  useEffect(() => () => clearTimeout(successHoldRef.current), []);

  // ── scoped CSS tokens (kept consistent with rest of the page) ──────────────
  const scopedVars = {
    "--cl2-gold":          "#B8922A",
    "--cl2-gold-light":    "#E0BA4B",
    "--cl2-gold-dim":      "rgba(184,146,42,0.08)",
    "--cl2-success":       "#10B981",
    "--cl2-success-dim":   "rgba(16,185,129,0.10)",
    "--cl2-danger":        "rgba(239,68,68,0.85)",
    "--cl2-surface":       "rgba(255,255,255,0.04)",
    "--cl2-surface-raised":"rgba(255,255,255,0.07)",
    "--cl2-border":        "rgba(255,255,255,0.10)",
    "--cl2-border-subtle": "rgba(255,255,255,0.06)",
    "--cl2-t1":            "#F8FAFC",
    "--cl2-t2":            "rgba(248,250,252,0.70)",
    "--cl2-t3":            "rgba(248,250,252,0.40)",
  };

  // common style helpers
  const fieldLabel = {
    display: "block",
    fontFamily: "'Fira Code', monospace",
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.10em",
    textTransform: "uppercase",
    color: "var(--cl2-t3)",
    marginBottom: 8,
  };
  const fieldInput = (extra = {}) => ({
    width: "100%",
    boxSizing: "border-box",
    background: "rgba(255,255,255,0.035)",
    border: "1px solid var(--cl2-border)",
    borderRadius: 14,
    padding: "13px 16px",
    fontFamily: "'Fira Code', monospace",
    fontSize: 14,
    color: "var(--cl2-t1)",
    outline: "none",
    boxShadow: "0 1px 0 rgba(255,255,255,0.04) inset",
    transition: "border-color 0.2s, box-shadow 0.2s, background 0.2s",
    WebkitAppearance: "none",
    ...extra,
  });
  const inputFocus = (e) => {
    e.target.style.borderColor = "var(--cl2-gold)";
    e.target.style.background = "rgba(184,146,42,0.05)";
    e.target.style.boxShadow = "0 0 0 3px var(--cl2-gold-dim)";
  };
  const inputBlur = (e) => {
    e.target.style.borderColor = "var(--cl2-border)";
    e.target.style.background = "rgba(255,255,255,0.035)";
    e.target.style.boxShadow = "0 1px 0 rgba(255,255,255,0.04) inset";
  };

  const onKeyActivate = (fn) => (e) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); fn(); }
  };

  return (
    <section
      id="calculator"
      data-testid="calculator-section"
      style={{
        position: "relative",
        padding: "clamp(72px, 8vw, 110px) 24px clamp(90px, 8vw, 120px)",
        background: "var(--bg-void, #0A0818)",
        ...scopedVars,
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            width: "min(680px, 92vw)",
            height: "min(680px, 92vw)",
            left: "50%",
            top: -360,
            transform: "translateX(-50%)",
            background: "radial-gradient(circle, rgba(184,146,42,0.18) 0%, rgba(184,146,42,0.00) 60%)",
            filter: "blur(2px)",
          }}
        />
        <div
          style={{
            position: "absolute",
            width: "min(760px, 96vw)",
            height: "min(760px, 96vw)",
            left: "50%",
            bottom: -520,
            transform: "translateX(-50%)",
            background: "radial-gradient(circle, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.00) 60%)",
            filter: "blur(6px)",
            opacity: 0.65,
          }}
        />
      </div>

      <div style={{ maxWidth: 920, margin: "0 auto 44px", textAlign: "center", position: "relative" }}>
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={vp}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            padding: "8px 12px",
            borderRadius: 999,
            border: "1px solid rgba(184,146,42,0.22)",
            background: "rgba(184,146,42,0.08)",
            color: "#ffffff",
            fontFamily: '"Syne", system-ui, -apple-system, sans-serif',
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            marginBottom: 16,
          }}
        >
          <span style={{ width: 8, height: 8, borderRadius: 999, background: "var(--cl2-gold)" }} />
          Free recovery estimate
        </motion.div>
        <motion.h2
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={vp}
          style={{
            fontFamily: "'Cabinet Grotesk', sans-serif",
            fontWeight: 800,
            fontSize: "clamp(30px, 4.5vw, 48px)",
            lineHeight: 1.06,
            letterSpacing: "-0.03em",
            color: "var(--cl2-t1)",
            margin: 0,
          }}
        >
          Recover failed payments — see your monthly revenue at risk.
        </motion.h2>
        <motion.p
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={vp}
          style={{
            fontFamily: "'Outfit', sans-serif",
            fontSize: "clamp(14px, 1.6vw, 16px)",
            color: "var(--cl2-t2)",
            marginTop: 14,
            lineHeight: 1.7,
          }}
        >
          Enter a few details. Get an instant estimate, then book a free recovery audit call.
        </motion.p>
        <div
          className="calc-benefits"
          style={{
            marginTop: 24,
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: 12,
          }}
        >
          {[
            {
              Icon: Timer,
              label: "25-second estimate",
              sub: "See at-risk revenue instantly",
              accent: {
                fg: "#38BDF8",
                bg: "rgba(56,189,248,0.12)",
                border: "rgba(56,189,248,0.22)",
                line: "rgba(56,189,248,0.85)",
              },
            },
            {
              Icon: Lock,
              label: "No credit card",
              sub: "No commitment to get your numbers",
              accent: {
                fg: "#A78BFA",
                bg: "rgba(167,139,250,0.12)",
                border: "rgba(167,139,250,0.22)",
                line: "rgba(167,139,250,0.85)",
              },
            },
            {
              Icon: ShieldCheck,
              label: "Secure & POPIA-ready",
              sub: "Built for SA compliance",
              accent: {
                fg: "#34D399",
                bg: "rgba(52,211,153,0.12)",
                border: "rgba(52,211,153,0.22)",
                line: "rgba(52,211,153,0.85)",
              },
            },
          ].map(({ Icon, label, sub, accent }) => (
            <div
              key={label}
              className="calc-benefit"
              style={{
                textAlign: "left",
                padding: "14px 14px",
                borderRadius: 16,
                border: "1px solid rgba(255,255,255,0.08)",
                borderTop: `2px solid ${accent.line}`,
                background: "rgba(255,255,255,0.02)",
                boxShadow: "0 14px 34px rgba(0,0,0,0.28)",
                transition: "transform 0.18s ease, border-color 0.18s ease, background 0.18s ease",
              }}
            >
              <div className="calc-benefit-row" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 12,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: accent.bg,
                    border: `1px solid ${accent.border}`,
                  }}
                >
                  <Icon size={16} color={accent.fg} strokeWidth={1.9} />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <div style={{
                    fontFamily: "'Outfit', sans-serif",
                    fontSize: 14,
                    fontWeight: 600,
                    color: "var(--cl2-t1)",
                    letterSpacing: "-0.01em",
                    lineHeight: 1.2,
                  }}>
                    {label}
                  </div>
                  <div style={{
                    fontFamily: "'Outfit', sans-serif",
                    fontSize: 12,
                    color: "var(--cl2-t3)",
                    lineHeight: 1.35,
                  }}>
                    {sub}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Layout */}
      <div
        className="calc-inner"
        style={{
          maxWidth: showResults ? 1120 : 700,
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: showResults ? "minmax(0,1fr) minmax(0,1fr)" : "minmax(0,1fr)",
          gap: showResults ? 24 : 0,
          alignItems: "start",
        }}
      >
        {hasApplied ? (
          <motion.div
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={vp}
            className="calc-left-card"
            style={{
              background: "linear-gradient(180deg, rgba(255,255,255,0.055) 0%, rgba(255,255,255,0.032) 100%)",
              backdropFilter: "blur(24px) saturate(160%)",
              WebkitBackdropFilter: "blur(24px) saturate(160%)",
              border: "1px solid var(--cl2-border)",
              borderRadius: 20,
              padding: 32,
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 320,
              gap: 12,
              boxShadow: "0 26px 70px rgba(0,0,0,0.48)",
            }}
            data-testid="calc-applied"
          >
            <CalendarCheck size={28} color="#10B981" strokeWidth={2} />
            <div
              style={{
                fontFamily: "'Cabinet Grotesk', sans-serif",
                fontWeight: 800,
                fontSize: 22,
                color: "var(--cl2-t1)",
                letterSpacing: "-0.02em",
              }}
            >
              You have applied already.
            </div>
            <div
              style={{
                fontFamily: "'Outfit', sans-serif",
                fontSize: 14,
                color: "var(--cl2-t2)",
                lineHeight: 1.6,
                maxWidth: 420,
              }}
            >
              We’ve received your details. If you need to update anything, contact support.
            </div>
          </motion.div>
        ) : (
          <>
            {/* ══════════════ LEFT — FORM ══════════════ */}
            <motion.form
              onSubmit={handleSubmit}
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={vp}
              className="calc-left-card"
              style={{
                background: "linear-gradient(180deg, rgba(255,255,255,0.055) 0%, rgba(255,255,255,0.032) 100%)",
                backdropFilter: "blur(24px) saturate(160%)",
                WebkitBackdropFilter: "blur(24px) saturate(160%)",
                border: "1px solid var(--cl2-border)",
                borderRadius: 20,
                padding: 32,
                position: showResults ? "sticky" : "relative",
                top: showResults ? 100 : "auto",
                boxShadow: "0 26px 70px rgba(0,0,0,0.48)",
              }}
            >
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                marginBottom: 22,
              }}>
                <div style={{
                  fontFamily: "'Fira Code', monospace", fontSize: 10, fontWeight: 600,
                  letterSpacing: "0.10em", textTransform: "uppercase",
                  color: "var(--cl2-t3)",
                }}>
                  Recovery calculator
                </div>
                <div style={{
                  fontFamily: "'Fira Code', monospace",
                  fontSize: 10,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "rgba(248,250,252,0.55)",
                  padding: "6px 10px",
                  borderRadius: 999,
                  border: "1px solid rgba(255,255,255,0.10)",
                  background: "rgba(255,255,255,0.03)",
                  whiteSpace: "nowrap",
                }}>
                  Step 1 of 2
                </div>
              </div>

          {/* ── Field 1: Email ─────────────────────────────────────────── */}
          <div style={{ marginBottom: 20 }}>
            <label htmlFor="cl-email" style={fieldLabel}>WORK EMAIL ADDRESS *</label>
            <input
              id="cl-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={(e) => { onEmailBlur(); inputBlur(e); }}
              onFocus={inputFocus}
              placeholder="you@company.co.za"
              data-testid="calc-email"
              style={fieldInput(showEmailError ? { borderColor: "var(--cl2-danger)" } : {})}
            />
            {showEmailError ? (
              <div style={{
                fontFamily: "'Outfit', sans-serif", fontSize: 12,
                color: "var(--cl2-danger)", marginTop: 6,
              }}>
                Enter a valid email address.
              </div>
            ) : (
              <div style={{
                fontFamily: "'Fira Code', monospace", fontSize: 10,
                color: "var(--cl2-t3)", marginTop: 6,
              }}>
                Your recovery estimate will be sent to this address.
              </div>
            )}
          </div>

          {/* ── Field 2: Phone (optional) ──────────────────────────────── */}
          <div style={{ marginBottom: 20 }}>
            <label htmlFor="cl-phone" style={fieldLabel}>TELEPHONE NUMBER</label>
            <input
              id="cl-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onFocus={inputFocus}
              onBlur={inputBlur}
              placeholder="+27 82 000 0000"
              data-testid="calc-phone"
              style={fieldInput()}
            />
            <div style={{
              fontFamily: "'Fira Code', monospace", fontSize: 10,
              color: "var(--cl2-t3)", marginTop: 6,
            }}>
              Optional — we may use this to confirm your activation booking.
            </div>
          </div>

          {/* ── Field 3: Business type ─────────────────────────────────── */}
          <div style={{ marginBottom: 24 }}>
            <span style={fieldLabel}>YOUR BUSINESS TYPE *</span>
            <div
              className="calc-bt-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                gap: 8,
              }}
            >
              {BUSINESS_TYPES.map(({ id, Icon }) => {
                const sel = businessType === id;
                return (
                  <div
                    key={id}
                    className="calc-bt-item"
                    role="button"
                    tabIndex={0}
                    aria-pressed={sel}
                    onClick={() => setBusinessType(id)}
                    onKeyDown={onKeyActivate(() => setBusinessType(id))}
                    data-testid={`calc-bt-${id.replace(/\s+/g, "-")}`}
                    style={{
                      background: sel ? "var(--cl2-gold-dim)" : "var(--cl2-surface-raised)",
                      border: `1px solid ${sel ? "var(--cl2-gold)" : "var(--cl2-border-subtle)"}`,
                      borderRadius: 12,
                      padding: "12px 10px",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      cursor: "pointer",
                      transition: "all 0.18s",
                      outline: "none",
                      boxShadow: sel ? "0 0 0 3px rgba(184,146,42,0.08)" : "none",
                    }}
                  >
                    <Icon size={15} color={sel ? "var(--cl2-gold-light)" : "var(--cl2-t3)"} strokeWidth={2} />
                    <span className="calc-bt-label" style={{
                      fontFamily: "'Outfit', sans-serif",
                      fontSize: 13,
                      fontWeight: 500,
                      color: sel ? "var(--cl2-t1)" : "var(--cl2-t2)",
                      lineHeight: 1.2,
                    }}>
                      {id}
                    </span>
                  </div>
                );
              })}
            </div>

            <AnimatePresence>
              {businessType === "Other" && (
                <motion.div
                  key="bt-other"
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.3, ease: EASE }}
                  style={{ marginTop: 10 }}
                >
                  <span style={{ ...fieldLabel, marginBottom: 6, fontSize: 10 }}>
                    PLEASE SPECIFY
                  </span>
                  <input
                    type="text"
                    value={businessTypeCustom}
                    onChange={(e) => setBusinessTypeCustom(e.target.value)}
                    placeholder="Describe your business type..."
                    onFocus={inputFocus}
                    onBlur={inputBlur}
                    data-testid="calc-bt-other-specify"
                    style={fieldInput()}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* ── Field 4: Subscriber count ──────────────────────────────── */}
          <div style={{ marginBottom: 24 }}>
            <label htmlFor="cl-subs" style={fieldLabel}>
              MONTHLY SUBSCRIBERS / ACTIVE ACCOUNTS *
            </label>
            <input
              id="cl-subs"
              type="text"
              inputMode="numeric"
              value={subscriberInput}
              onChange={onSubscriberChange}
              onFocus={inputFocus}
              onBlur={inputBlur}
              placeholder="e.g. 850"
              data-testid="calc-subscribers"
              style={fieldInput({
                fontSize: 20,
                fontWeight: 600,
                padding: "14px 18px",
                letterSpacing: "-0.01em",
              })}
            />
            {subscriberAdvisory ? (
              <div style={{
                fontFamily: "'Outfit', sans-serif", fontSize: 12,
                color: "var(--cl2-gold-light)", marginTop: 6,
              }}>
                {subscriberAdvisory}
              </div>
            ) : (
              <div style={{
                fontFamily: "'Fira Code', monospace", fontSize: 10,
                color: "var(--cl2-t3)", marginTop: 6,
              }}>
                Total active paying subscribers on debit order or recurring billing.
              </div>
            )}
          </div>

          {/* ── Field 5: Processor ─────────────────────────────────────── */}
          <div style={{ marginBottom: 28 }}>
            <span style={fieldLabel}>PAYMENT PROCESSOR</span>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {PROCESSORS.map((p) => {
                const sel = processor === p;
                return (
                  <div
                    key={p}
                    role="button"
                    tabIndex={0}
                    aria-pressed={sel}
                    onClick={() => setProcessor(p)}
                    onKeyDown={onKeyActivate(() => setProcessor(p))}
                    data-testid={`calc-proc-${p.replace(/\s+/g, "-")}`}
                    style={{
                      background: sel ? "var(--cl2-gold-dim)" : "rgba(255,255,255,0.04)",
                      border: `1px solid ${sel ? "var(--cl2-gold)" : "var(--cl2-border-subtle)"}`,
                      borderRadius: 999,
                      padding: "8px 16px",
                      fontFamily: "'Fira Code', monospace",
                      fontSize: 12,
                      color: sel ? "var(--cl2-t1)" : "var(--cl2-t3)",
                      fontWeight: sel ? 600 : 400,
                      cursor: "pointer",
                      userSelect: "none",
                      transition: "all 0.16s",
                      outline: "none",
                    }}
                  >
                    {p}
                  </div>
                );
              })}
            </div>

            <AnimatePresence>
              {processor === "Other" && (
                <motion.div
                  key="proc-other"
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.3, ease: EASE }}
                  style={{ marginTop: 10 }}
                >
                  <span style={{ ...fieldLabel, marginBottom: 6, fontSize: 10 }}>
                    PROCESSOR NAME
                  </span>
                  <input
                    type="text"
                    value={processorCustom}
                    onChange={(e) => setProcessorCustom(e.target.value)}
                    placeholder="Type your processor name..."
                    onFocus={inputFocus}
                    onBlur={inputBlur}
                    data-testid="calc-proc-other-specify"
                    style={fieldInput()}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* ── Submit ─────────────────────────────────────────────────── */}
          <motion.button
            type="submit"
            disabled={!isFormValid || submitStatus === "loading"}
            whileHover={isFormValid && submitStatus === "idle" ? {
              translateY: -1,
              boxShadow: "0 8px 28px rgba(184,146,42,0.30), 0 2px 8px rgba(0,0,0,0.40)",
            } : {}}
            whileTap={isFormValid && submitStatus === "idle" ? { scale: 0.985 } : {}}
            data-testid="calc-submit"
            style={{
              width: "100%",
              background: submitStatus === "success"
                ? "var(--cl2-success)"
                : "linear-gradient(135deg, #E0BA4B 0%, #B8922A 100%)",
              color: submitStatus === "success" ? "#fff" : "#0A0818",
              fontFamily: "'Fira Code', monospace",
              fontWeight: 700,
              fontSize: 14,
              letterSpacing: "0.04em",
              padding: "15px 28px",
              borderRadius: 12,
              border: "none",
              cursor: isFormValid && submitStatus !== "loading" ? "pointer" : "not-allowed",
              opacity: !isFormValid ? 0.4 : 1,
              pointerEvents: !isFormValid ? "none" : "auto",
              transition: "all 0.25s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
            }}
          >
            {submitStatus === "loading" && (
              <>
                <Loader2 size={16} className="cl-spin" />
                <span>Saving…</span>
              </>
            )}
            {submitStatus === "success" && (
              <>
                <CheckCircle size={16} />
                <span>Saved — see your estimate</span>
              </>
            )}
            {submitStatus === "idle" && <span>Calculate My Recovery →</span>}
          </motion.button>

          {/* Trust row */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            gap: 18, marginTop: 14, flexWrap: "wrap",
          }}>
            {[
              { Icon: Lock,        label: "No credit card" },
              { Icon: Timer,       label: "Estimate in seconds" },
              { Icon: ShieldCheck, label: "POPIA compliant" },
            ].map(({ Icon, label }) => (
              <div key={label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <Icon size={12} color="var(--cl2-t3)" strokeWidth={2} />
                <span style={{
                  fontFamily: "'Fira Code', monospace",
                  fontSize: 11, color: "var(--cl2-t3)",
                }}>{label}</span>
              </div>
            ))}
          </div>

          <div
            className="calc-privacy"
            style={{
              marginTop: 10,
              textAlign: "center",
              fontFamily: "'Outfit', sans-serif",
              fontSize: 12,
              color: "rgba(248,250,252,0.55)",
              lineHeight: 1.5,
            }}
          >
            No spam — we only email your estimate and audit details.
          </div>
            </motion.form>

            {showResults ? (
          <motion.div
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={vp}
            className="calc-right-card"
            style={{
              background: "linear-gradient(180deg, rgba(255,255,255,0.055) 0%, rgba(255,255,255,0.032) 100%)",
              backdropFilter: "blur(24px) saturate(160%)",
              WebkitBackdropFilter: "blur(24px) saturate(160%)",
              border: "1px solid var(--cl2-border)",
              borderRadius: 20,
              padding: 32,
              minHeight: 540,
              boxShadow: "0 26px 70px rgba(0,0,0,0.48)",
            }}
          >
          <AnimatePresence mode="wait">
            {!result ? (
              // ── IDLE STATE ──────────────────────────────────────────
              <motion.div
                key="idle"
                variants={fadeIn} initial="hidden" animate="visible" exit="hidden"
                style={{
                  display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center",
                  padding: "60px 32px", textAlign: "center", gap: 12,
                  minHeight: 460,
                }}
              >
                <CalcIcon size={28} color="var(--cl2-t3)" strokeWidth={1.5} />
                <p style={{
                  fontFamily: "'Outfit', sans-serif",
                  fontSize: 14, color: "var(--cl2-t3)",
                  margin: "8px 0 0", lineHeight: 1.6, maxWidth: 320,
                }}>
                  Select your business type and enter your subscriber count to see your recovery estimate.
                </p>
              </motion.div>
            ) : (
              // ── SHOWING STATE ───────────────────────────────────────
              <motion.div
                key="showing"
                variants={fadeUp} initial="hidden" animate="visible"
              >
                <div style={{
                  fontFamily: "'Fira Code', monospace",
                  fontSize: 10, fontWeight: 600,
                  letterSpacing: "0.10em", textTransform: "uppercase",
                  color: "var(--cl2-t3)", marginBottom: 20,
                }}>
                  YOUR RECOVERY ESTIMATE
                </div>

                {/* Row 1 — At Risk */}
                <div style={{
                  padding: "18px 0",
                  borderBottom: "1px solid rgba(255,255,255,0.06)",
                }}>
                  <div style={{
                    fontFamily: "'Fira Code', monospace", fontSize: 10,
                    letterSpacing: "0.08em", textTransform: "uppercase",
                    color: "var(--cl2-t3)", marginBottom: 6,
                  }}>
                    MONTHLY REVENUE AT RISK
                  </div>
                  <AnimatedRand value={result.monthlyAtRisk} fontSize={38} weight={800} />
                  <div style={{
                    fontFamily: "'Outfit', sans-serif", fontSize: 12,
                    color: "var(--cl2-t3)", marginTop: 4,
                  }}>
                    {Math.round(result.failureRateUsed * 100)}% estimated failure rate for {businessType}
                  </div>
                </div>

                {/* Row 2 — Conservative Recovery */}
                <div style={{
                  padding: "18px 0",
                  borderBottom: "1px solid rgba(255,255,255,0.06)",
                }}>
                  <div style={{
                    fontFamily: "'Fira Code', monospace", fontSize: 10,
                    letterSpacing: "0.08em", textTransform: "uppercase",
                    color: "var(--cl2-t3)", marginBottom: 6,
                  }}>
                    CONSERVATIVE RECOVERY (55%)
                  </div>
                  <span style={{
                    background: "linear-gradient(135deg, #E0BA4B 0%, #B8922A 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                    display: "inline-block",
                  }}>
                    <AnimatedRand value={result.conservativeRecovery} fontSize={30} weight={700} color="transparent" />
                  </span>
                  <div style={{
                    fontFamily: "'Outfit', sans-serif", fontSize: 12,
                    color: "var(--cl2-t3)", marginTop: 4,
                  }}>
                    At Crown Ledger’s verified 55% recovery rate
                  </div>
                </div>

                {/* Row 3 — Fee */}
                <div style={{
                  padding: "18px 0",
                  borderBottom: "1px solid rgba(255,255,255,0.06)",
                }}>
                  <div style={{
                    fontFamily: "'Fira Code', monospace", fontSize: 10,
                    letterSpacing: "0.08em", textTransform: "uppercase",
                    color: "var(--cl2-t3)", marginBottom: 6,
                  }}>
                    CROWN LEDGER FEE (10%)
                  </div>
                  <AnimatedRand value={result.crownLedgerFee} fontSize={22} weight={600} color="var(--cl2-t2)" />
                  <div style={{
                    fontFamily: "'Outfit', sans-serif", fontSize: 12,
                    color: "var(--cl2-t3)", marginTop: 4,
                  }}>
                    Performance fee — charged on recovered revenue only
                  </div>
                </div>

                {/* Row 4 — Net (highlight) */}
                <div style={{
                  background: "rgba(184,146,42,0.06)",
                  border: "1px solid rgba(184,146,42,0.20)",
                  borderRadius: 14,
                  padding: "20px 22px",
                  marginTop: 8,
                }}>
                  <div style={{
                    fontFamily: "'Fira Code', monospace", fontSize: 10,
                    fontWeight: 600, letterSpacing: "0.10em", textTransform: "uppercase",
                    color: "var(--cl2-gold)", marginBottom: 8,
                  }}>
                    ESTIMATED MONTHLY NET TO YOUR BUSINESS
                  </div>
                  <span style={{
                    background: "linear-gradient(135deg, #E0BA4B 0%, #B8922A 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                    display: "inline-block",
                  }}>
                    <AnimatedRand value={result.netToClient} fontSize={42} weight={800} color="transparent" />
                  </span>
                  <div style={{
                    fontFamily: "'Outfit', sans-serif", fontSize: 13,
                    color: "var(--cl2-t2)", marginTop: 6,
                  }}>
                    {formatRand(result.annualNetToClient)} recovered annually at this rate
                  </div>
                </div>

                {/* Footnote */}
                <div style={{
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.06)",
                  borderRadius: 10,
                  padding: "14px 16px",
                  marginTop: 20,
                  fontFamily: "'Fira Code', monospace",
                  fontSize: 10,
                  color: "var(--cl2-t3)",
                  lineHeight: 1.6,
                }}>
                  Estimates use an average subscription value of R{formatInt(result.arpuUsed)} and a {Math.round(result.failureRateUsed * 100)}% failure rate for {businessType} businesses in South Africa. Based on PASA dispute data and BankservAfrica 2024 figures. Your free recovery audit will use your actual transaction history.
                </div>

                {/* Post-submit success banner with loading dots */}
                <AnimatePresence>
                  {submitStatus === "success" && !showCalModal && !showThanks && (
                    <motion.div
                      key="success-banner"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.4 }}
                      style={{
                        marginTop: 18,
                        background: "rgba(16,185,129,0.08)",
                        border: "1px solid rgba(16,185,129,0.25)",
                        borderRadius: 12,
                        padding: "14px 16px",
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                      }}
                    >
                      <Loader2 size={14} className="cl-spin" color="#10B981" />
                      <span style={{
                        fontFamily: "'Outfit', sans-serif",
                        fontSize: 13,
                        color: "var(--cl2-t1)",
                        lineHeight: 1.5,
                      }}>
                        Your account has been activated — redirecting you to schedule a setup call…
                      </span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
          </AnimatePresence>
          </motion.div>
        ) : null}
          </>
        )}
      </div>

      {/* ══════════════ CAL.COM MODAL ══════════════ */}
      <AnimatePresence>
        {showCalModal && (
          <motion.div
            key="cal-backdrop"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => { if (e.target === e.currentTarget) completeApplication(); }}
            style={{
              position: "fixed", inset: 0,
              background: "rgba(8,6,18,0.78)",
              backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
              zIndex: 220,
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: 20,
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.25, ease: EASE }}
              style={{
                position: "relative",
                width: "100%",
                maxWidth: 980,
                height: "min(86vh, 760px)",
                background: "#0F0E1A",
                border: "1px solid rgba(255,255,255,0.10)",
                borderRadius: 18,
                overflow: "hidden",
                boxShadow: "0 32px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(0,0,0,0.06)",
              }}
            >
              <button
                aria-label="Close booking"
                onClick={completeApplication}
                style={{
                  position: "absolute", top: 12, right: 12, zIndex: 5,
                  width: 34, height: 34, borderRadius: "50%",
                  background: "rgba(255,255,255,0.10)", border: "none",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <X size={16} color="#F8FAFC" />
              </button>
              <div ref={calRootRef} style={{ width: "100%", height: "100%" }}>
                {CAL_LINK ? (
                  <CalEmbed
                    calLink={CAL_LINK}
                    namespace={CAL_NAMESPACE}
                    prefill={{ name: email.split("@")[0], email, phone }}
                  />
                ) : (
                  <CalFallback
                    email={email}
                    onContinue={completeApplication}
                  />
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ══════════════ THANK YOU POPUP ══════════════ */}
      <AnimatePresence>
        {showThanks && (
          <motion.div
            key="thanks-backdrop"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => { if (e.target === e.currentTarget) setShowThanks(false); }}
            style={{
              position: "fixed", inset: 0,
              background: "rgba(8,6,18,0.78)",
              backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
              zIndex: 230,
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: 20,
            }}
          >
            <motion.div
              role="dialog" aria-modal="true"
              initial={{ opacity: 0, scale: 0.94, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.3, ease: EASE }}
              style={{
                background: "#FFFFFF",
                borderRadius: 22,
                padding: 36,
                width: "100%",
                maxWidth: 440,
                textAlign: "center",
                boxShadow: "0 32px 80px rgba(0,0,0,0.5)",
              }}
            >
              <div style={{
                width: 56, height: 56,
                margin: "0 auto 16px",
                borderRadius: "50%",
                background: "rgba(16,185,129,0.12)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <CalendarCheck size={28} color="#10B981" strokeWidth={2} />
              </div>
              <h3 style={{
                fontFamily: "'Cabinet Grotesk', sans-serif",
                fontWeight: 700, fontSize: 24, color: "#0F0E1A",
                margin: "0 0 10px",
              }}>
                Thank you
              </h3>
              <p style={{
                fontFamily: "'Outfit', sans-serif",
                fontSize: 15, color: "#374151",
                lineHeight: 1.6, margin: "0 0 24px",
              }}>
                We will be in touch with you soon. Check your inbox for the calendar invite and pre-call brief.
              </p>
              <button
                onClick={() => setShowThanks(false)}
                style={{
                  background: "#0F0E1A", color: "#fff",
                  fontFamily: "'Fira Code', monospace", fontWeight: 600, fontSize: 13,
                  letterSpacing: "0.04em",
                  padding: "12px 28px", borderRadius: 10, border: "none",
                  cursor: "pointer",
                }}
              >
                Done
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Scoped CSS — spinner + responsive ─────────────────────────── */}
      <style>{`
        @keyframes cl-spin { to { transform: rotate(360deg); } }
        .cl-spin { animation: cl-spin 0.9s linear infinite; }

        .calc-left-card, .calc-right-card {
          position: relative;
          overflow: hidden;
        }
        .calc-left-card::before, .calc-right-card::before {
          content: "";
          position: absolute;
          inset: 0;
          pointer-events: none;
          background:
            radial-gradient(820px 240px at 16% 0%, rgba(184,146,42,0.14) 0%, rgba(184,146,42,0.00) 60%),
            radial-gradient(640px 220px at 84% 0%, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.00) 62%);
          opacity: 0.9;
          transform: translateZ(0);
        }
        .calc-left-card > *, .calc-right-card > * {
          position: relative;
          z-index: 1;
        }

        .calc-benefit:hover {
          transform: translateY(-2px);
          border-color: rgba(255,255,255,0.16) !important;
          background: rgba(255,255,255,0.04) !important;
        }

        @media (max-width: 880px) {
          .calc-inner { grid-template-columns: 1fr !important; gap: 20px !important; }
          .calc-benefits { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          .calc-left-card { position: static !important; top: auto !important; padding: 26px !important; }
          .calc-right-card { padding: 26px !important; min-height: auto !important; }
        }
        @media (max-width: 560px) {
          .calc-benefits { grid-template-columns: 1fr !important; }
          .calc-benefit { padding: 12px 12px !important; border-radius: 14px !important; }
          .calc-benefit-row { gap: 12px !important; }
        }
        @media (max-width: 480px) {
          #calculator { padding: 72px 16px 84px !important; }
          .calc-benefits { gap: 10px !important; }
          .calc-left-card, .calc-right-card { padding: 22px !important; border-radius: 16px !important; }
          .calc-bt-grid { grid-template-columns: 1fr 1fr !important; }
          .calc-bt-item { padding: 10px 10px !important; }
          .calc-bt-label { font-size: 12px !important; }
          .calc-privacy { font-size: 12px !important; }
        }
      `}</style>
    </section>
  );
}

// ─── Cal.com embed wrapper ───────────────────────────────────────────────────
// Loaded lazily because the embed package brings its own runtime.
function CalEmbed({ calLink, namespace, prefill }) {
  const [Cmp, setCmp] = useState(null);

  useEffect(() => {
    let mounted = true;
    import("@calcom/embed-react").then((mod) => {
      if (mounted) setCmp(() => mod.default);
    }).catch((err) => {
      // eslint-disable-next-line no-console
      console.warn("[CalEmbed] failed to load @calcom/embed-react", err);
    });
    return () => { mounted = false; };
  }, []);

  if (!Cmp) {
    return (
      <div style={{
        width: "100%", height: "100%",
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", gap: 12,
        color: "rgba(248,250,252,0.55)", fontFamily: "'Fira Code', monospace",
        fontSize: 12,
      }}>
        <Loader2 size={18} className="cl-spin" />
        Loading scheduler…
      </div>
    );
  }

  return (
    <Cmp
      calLink={calLink}
      {...(namespace ? { namespace } : {})}
      style={{ width: "100%", height: "100%", overflow: "scroll" }}
      config={{
        layout: "month_view",
        theme: "dark",
        useSlotsViewOnSmallScreen: "true",
        ...(prefill?.email ? { email: prefill.email } : {}),
        ...(prefill?.name  ? { name:  prefill.name  } : {}),
      }}
    />
  );
}

// ─── Cal fallback — used while a real Cal.com link isn't configured ─────────
function CalFallback({ email, onContinue }) {
  return (
    <div style={{
      width: "100%", height: "100%",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      padding: "40px 28px", textAlign: "center",
      background: "linear-gradient(160deg, #0F0E1A 0%, #0A0818 100%)",
    }}>
      <div style={{
        width: 64, height: 64, marginBottom: 18,
        borderRadius: "50%",
        background: "rgba(184,146,42,0.10)",
        border: "1px solid rgba(184,146,42,0.30)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <CalendarCheck size={28} color="#E0BA4B" strokeWidth={1.8} />
      </div>
      <h3 style={{
        fontFamily: "'Cabinet Grotesk', sans-serif",
        fontWeight: 700, fontSize: 24,
        color: "#F8FAFC", margin: "0 0 10px",
        letterSpacing: "-0.02em",
      }}>
        Schedule Your Activation Call
      </h3>
      <p style={{
        fontFamily: "'Outfit', sans-serif",
        fontSize: 14, color: "rgba(248,250,252,0.70)",
        lineHeight: 1.65, maxWidth: 440, margin: "0 0 8px",
      }}>
        We received your details for <strong style={{ color: "#F8FAFC" }}>{email || "your account"}</strong>.
        Our setup specialist will reach out within 24 hours to confirm a 12-minute activation call.
      </p>
      <p style={{
        fontFamily: "'Fira Code', monospace",
        fontSize: 11, color: "rgba(248,250,252,0.40)",
        marginTop: 14, marginBottom: 28,
        letterSpacing: "0.04em",
      }}>
        Live scheduling unlocks shortly — your slot is reserved.
      </p>
      <button
        onClick={onContinue}
        style={{
          background: "linear-gradient(135deg, #E0BA4B 0%, #B8922A 100%)",
          color: "#0A0818",
          fontFamily: "'Fira Code', monospace",
          fontWeight: 700, fontSize: 13, letterSpacing: "0.04em",
          padding: "13px 32px", borderRadius: 12, border: "none",
          cursor: "pointer",
          boxShadow: "0 8px 28px rgba(184,146,42,0.30)",
        }}
      >
        Continue →
      </button>
    </div>
  );
}
